import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Hotel } from '@travel/icons/service';
import JapanStandardPopup from '@travel/traveler-core/components/JapanStandardPopup';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { numOfGuestParser, toSEOSearchString } from '@travel/traveler-core/utils';
import { cx, isNotEmptyArray } from '@travel/utils';

import Breadcrumb from 'components/Breadcrumb';
import CouponAccordion from 'components/CouponAccordion';
import DataLayer from 'components/DataLayer';
import Pagination from 'components/Pagination';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { OFFER_TYPE } from 'constants/salesPromotion';
import Footer from 'core/App/components/Footer';
import paths from 'core/universalRouter/paths';
import { fetchMoreProviders } from 'store/providerList/actions';
import {
  getIsFetchingMore,
  getItems,
  getPlaceName,
  getRequestOffsets,
  getSearchFacilities,
  getSelectedFeatures,
} from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';
import ProviderBox from '../ProviderBox';

import { pushLocation } from 'store/__router/actions';
import { setSigninPopupRAT } from 'store/banner/actions';

import { Translate } from 'core/translate';
import { SearchFormQuery } from 'ProviderList-Types';
import { getRequestBody } from '../../resolver';

import { getIsSigninPopupVisible } from 'store/banner/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';

import styles from './providerList.module.scss';

type Props = {
  /** Object of current page identifier */
  params: { pageIdentifier: string };
  /** Object of current page query */
  query?: SearchFormQuery;
  /** Callback to be called when provider item is being hovered */
  onMouseOver?: (itemId: string) => void;
  /** Flag to define whether the map is expanded or not */
  isExpandedMap?: boolean;
  /** Callback to be called when remove button of coupon name is clicked */
  onRemoveCoupon?: () => void;
};

export type ListTypes = {
  offset?: number;
  limit?: number;
  page?: number;
};

function ProviderList(props: Props) {
  const { query } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  // const language = useSelector(getLanguage);
  const limitations = useSelector(getLimitationsForCurrentMarket);

  const couponId = props.query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));
  const {
    providers,
    page,
    offset,
    nextOffset,
    limit,
    total,
    areaInfo,
    areaName,
    breadcrumb,
  } = useSelector(getItems);

  const requestOffsets = useSelector(getRequestOffsets);
  // const placeType = useSelector(getPlaceType);
  const placeName = useSelector(getPlaceName);
  const isFetchingMore = useSelector(getIsFetchingMore);
  const searchFacilities: string = useSelector(getSearchFacilities(query?.features));
  const selectedFeatures = useSelector(getSelectedFeatures);

  // RAT Params values from providers
  const providerIds = providers ? providers.filter(item => item).map(item => item.id) : null;
  const salesPromotions = useMemo(
    () =>
      providers
        ? providers
            .map(item => item?.price?.salesPromotions?.map(sp => sp.id))
            .flat()
            .filter(spId => spId)
        : null,
    [providers],
  );

  const pageName =
    query?.startDate || query?.endDate
      ? 'search:search_results_dated_list'
      : 'search:search_results_undated_list';
  const onPage = query?.startDate || query?.endDate ? 'providerListDated' : 'providerListUndated';
  const previousOffset = requestOffsets[page - 1] ?? Math.max(offset - limit, 0);
  const isMoreThanOnePage = total > limit;

  const hasJapanStandardProvider = providers?.some(provider => provider?.isJapanStandard);

  const onClickPaginationButton = useCallback(
    (nextPaginationOffset, limit, action) => {
      const pageRules = props.isExpandedMap
        ? action === 'next'
          ? page + 1
          : page - 1
        : nextPaginationOffset / limit + 1;

      const listProps = {
        params: props.params,
        query: {
          ...props.query,
          page: pageRules,
          offset: nextPaginationOffset,
        },
      };

      if (isMobile) {
        dispatch(fetchMoreProviders(getRequestBody({ ...listProps }, couponDetail)));
      } else {
        dispatch(
          pushLocation({
            pathname: paths.providerList.pathResolver(props.params.pageIdentifier),
            search: toSEOSearchString(listProps.query, limitations),
          }),
        );
      }
    },
    [couponDetail, dispatch, isMobile, props.params, props.query],
  );

  const paginationLabelResolver = (reviewCount: string, totalCount: number) => (
    <Translate
      id="Provider_List.Pagination.Hotel.Range"
      data={{
        index: reviewCount,
        num_properies: totalCount,
      }}
    />
  );

  const isSigninPopupVisible = useSelector(getIsSigninPopupVisible);
  useEffect(() => {
    dispatch(setSigninPopupRAT(RAT_PAGE_TYPE_OBJ[onPage]));
  }, [dispatch, onPage]);

  return (
    <section className={styles.container} data-testid="providerList-wrapper">
      <div className={styles.providerListHeader}>
        {isMobile && <div className={styles.swipeDownSign} />}
        <h1 className={cx(couponId && styles.total)} data-testid="providerList-title-text">
          {couponId ? (
            <Translate
              id="Coupon_Details.Search_Eligible"
              count={total}
              data={{
                num_eligible_hotels: total,
              }}
            />
          ) : (
            <>
              {/* TEM-2320: Hidden temporarily on release day 1 */}
              {/* <Translate
                id="Provider_List.Search_Results.Hotels"
                data={{ number_of_providers: getL10nNumber(language, total || 0) }}
                count={total}
              />
              &nbsp;
              <Translate
                id="Provider_List.Search_Results.Title"
                data={{ area: placeName, spot: placeName }}
                condition={() => `status == ${placeType?.toLowerCase()}_search`}
              /> */}
            </>
          )}
        </h1>
        {isMobile && (
          <>
            {couponId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
              <CouponAccordion
                className={styles.couponAccordion}
                onRemoveCoupon={props.onRemoveCoupon}
                couponId={couponId}
              />
            )}
          </>
        )}
      </div>
      <div className={isMobile ? styles.providerItemsSP : ''}>
        {isNotEmptyArray(providers) ? (
          <>
            {providers
              .filter(item => item)
              .map((item, i) => (
                <ProviderBox
                  index={i}
                  onPage={onPage}
                  key={item.id}
                  item={item}
                  query={props.query}
                  onMouseOver={props.onMouseOver}
                  isExpandedMapStyle={isMobile ? true : !!props.isExpandedMap}
                />
              ))}

            {isFetchingMore && (
              <WithSkeletonLoading
                className={styles.loaderWrapper}
                skeletonItemClassName={styles.loader}
                skeletonImageClassName={styles.loaderImage}
                isLoading={true}
                count={3}
                length="long"
                customStyle="textWithImageRow"
              />
            )}
          </>
        ) : (
          <div className={styles.noitems}>
            <Hotel size={60} color={'sonicSilver'} />
            <Translate id="Provider_List.Search_Results.No_Results" className={styles.title} />
            <Translate id="Provider_List.No_Results_Subheading" className={styles.subtitle} />
          </div>
        )}
      </div>
      {isNotEmptyArray(providers) ? (
        isMoreThanOnePage ? (
          <Pagination
            customStyle={isMobile ? 'loadmore' : props.isExpandedMap ? 'nextAndPrev' : 'pagination'}
            total={total}
            limit={limit}
            offset={offset}
            className={styles.loadmoreBox}
            callback={onClickPaginationButton}
            navigationType="simple"
            // NOTE: We have to use nextOffset data from API
            hasNext={Boolean(nextOffset) && nextOffset !== total}
            nextPageOffset={nextOffset}
            hasPrevious={page > 1}
            previousPageOffset={previousOffset}
            paginationLabelResolver={paginationLabelResolver}
          />
        ) : !props.isExpandedMap && !isMobile ? (
          <>
            <div className={styles.totalPropertiesTop} />
            <Translate
              className={styles.totalProperties}
              id="Provider_List.Pagination.Hotel.Totals"
              data={{ num_properties: total }}
              count={total}
            />
          </>
        ) : null
      ) : null}
      {/* Note: Common App <Footer /> will not be rendered. Since the map has been covered up the page, 
        there is no chance to see footer and breadcrumb. We need to define it in the content part. (Mobile only)
      */}
      {isMobile && (
        <>
          <Breadcrumb className={styles.breadcrumb} placeLayers={breadcrumb} />
          <Footer />
        </>
      )}
      {/* for japan quality SEO */}
      {hasJapanStandardProvider && <JapanStandardPopup isUsedForSEO={true} />}
      {!isSigninPopupVisible && (
        <DataLayer
          pageName={pageName}
          pageType="search"
          siteSection="search"
          shopIdList={providerIds?.join(',')}
          salesPromotions={salesPromotions?.join(',')}
          placeName={placeName}
          searchQuery={placeName}
          searchCondition={searchFacilities}
          numOfAdults={parseInt(query?.adults || '')}
          numOfChildren={query?.childrenAges?.split(',')?.length}
          numOfGuest={numOfGuestParser(query?.adults, query?.childrenAges)}
          childrenAges={query?.childrenAges}
          checkIn={query?.startDate}
          checkOut={query?.endDate}
          noOfRooms={query?.noOfUnits}
          propertyCountry={areaInfo?.country}
          hotelRegion={areaInfo?.region}
          propertyCity={areaInfo?.city}
          propertyDistrict={areaInfo?.district}
          areaName={areaName}
          searchFilters={selectedFeatures.map(feature => feature.managementName).join(',')}
        />
      )}
    </section>
  );
}

export default React.memo(ProviderList);
